import * as THREE from "three";
import { humanSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";

type Pose = "ready" | "throw" | "catch";
type Phase =
  | { kind: "hold"; holder: 0 | 1; t: number; dur: number }
  | { kind: "windup"; thrower: 0 | 1; t: number; dur: number }
  | { kind: "flight"; from: 0 | 1; t: number; dur: number }
  | { kind: "catch"; catcher: 0 | 1; t: number; dur: number };

/**
 * Ambient frisbee toss: two kids hold, flick, and catch a flat disc back and forth.
 */
export class ParkFrisbeeGame {
  readonly group = new THREE.Group();
  readonly midX: number;
  readonly midZ: number;
  private readonly kidA: THREE.Mesh;
  private readonly kidB: THREE.Mesh;
  private readonly disc: THREE.Mesh;
  private readonly shadowA: THREE.Mesh;
  private readonly shadowB: THREE.Mesh;
  private readonly ax: number;
  private readonly az: number;
  private readonly bx: number;
  private readonly bz: number;
  private readonly ay: number;
  private readonly by: number;
  private readonly readyA: ReturnType<typeof humanSprite>;
  private readonly throwA: ReturnType<typeof humanSprite>;
  private readonly catchA: ReturnType<typeof humanSprite>;
  private readonly readyB: ReturnType<typeof humanSprite>;
  private readonly throwB: ReturnType<typeof humanSprite>;
  private readonly catchB: ReturnType<typeof humanSprite>;
  private readonly discHue: number;
  private spin = 0;
  private phase: Phase;
  private jumpA = 0;
  private jumpB = 0;

  constructor(
    midX: number,
    midZ: number,
    groundY: (x: number, z: number) => number,
    variant = 0,
    phaseOffset = 0,
  ) {
    const half = 3.6 + (variant % 3) * 0.3;
    const ang = (variant % 4) * (Math.PI / 8);
    this.ax = midX - Math.cos(ang) * half;
    this.az = midZ - Math.sin(ang) * half;
    this.bx = midX + Math.cos(ang) * half;
    this.bz = midZ + Math.sin(ang) * half;
    this.midX = midX;
    this.midZ = midZ;
    this.ay = groundY(this.ax, this.az);
    this.by = groundY(this.bx, this.bz);
    this.discHue = [0xef5350, 0x42a5f5, 0x66bb6a, 0xffca28][variant % 4]!;

    this.readyA = humanSprite("frisbeeReady", variant);
    this.throwA = humanSprite("frisbeeThrow", variant);
    this.catchA = humanSprite("frisbeeCatch", variant);
    this.readyB = humanSprite("frisbeeReady", variant + 13);
    this.throwB = humanSprite("frisbeeThrow", variant + 13);
    this.catchB = humanSprite("frisbeeCatch", variant + 13);

    this.shadowA = makeShadow(this.readyA.width);
    this.shadowA.position.set(this.ax, this.ay + 0.02, this.az);
    this.shadowB = makeShadow(this.readyB.width);
    this.shadowB.position.set(this.bx, this.by + 0.02, this.bz);

    this.kidA = new THREE.Mesh(
      new THREE.PlaneGeometry(this.readyA.width, this.readyA.height),
      billboardMaterial(this.readyA.texture),
    );
    this.kidA.position.set(this.ax, this.ay + this.readyA.height * SPRITE_FOOT_Y, this.az);
    this.kidA.renderOrder = 3;

    this.kidB = new THREE.Mesh(
      new THREE.PlaneGeometry(this.readyB.width, this.readyB.height),
      billboardMaterial(this.readyB.texture),
    );
    this.kidB.position.set(this.bx, this.by + this.readyB.height * SPRITE_FOOT_Y, this.bz);
    this.kidB.renderOrder = 3;

    // Short cylinder is already a horizontal pancake (axis along Y).
    this.disc = new THREE.Mesh(
      new THREE.CylinderGeometry(0.32, 0.32, 0.045, 20),
      new THREE.MeshLambertMaterial({ color: this.discHue }),
    );
    this.disc.castShadow = false;
    this.disc.renderOrder = 4;

    this.group.add(this.shadowA, this.shadowB, this.kidA, this.kidB, this.disc);

    // Stagger games so they don't all throw in sync.
    const starter: 0 | 1 = phaseOffset > 0.5 ? 1 : 0;
    this.phase = {
      kind: "hold",
      holder: starter,
      t: 0,
      dur: 0.55 + Math.random() * 0.9 + phaseOffset * 0.4,
    };
  }

  /** True when any dog is within a few tiles of either kid. */
  dogIsClose(dogs: Iterable<{ poseX: number; poseY: number }>, radius = 4.2): boolean {
    for (const dog of dogs) {
      const lx = dog.poseX + 0.5;
      const lz = dog.poseY + 0.5;
      if (Math.hypot(lx - this.ax, lz - this.az) < radius) return true;
      if (Math.hypot(lx - this.bx, lz - this.bz) < radius) return true;
    }
    return false;
  }

  update(
    dtSec: number,
    timeMs: number,
    camera: THREE.Camera,
    dogNear = false,
  ): void {
    this.advance(dtSec);

    const aPose = poseFor(this.phase, 0);
    const bPose = poseFor(this.phase, 1);
    setHumanMap(
      this.kidA,
      aPose === "throw" ? this.throwA : aPose === "catch" ? this.catchA : this.readyA,
    );
    setHumanMap(
      this.kidB,
      bPose === "throw" ? this.throwB : bPose === "catch" ? this.catchB : this.readyB,
    );

    const releaseH = 1.18;
    const catchH = 1.12;
    const holdH = 0.72;
    const handReach = 0.55;

    // One shared 3D disc - only at the holder / in flight (never painted on sprites).
    this.disc.visible = true;
    if (this.phase.kind === "flight") {
      const u = Math.max(0, Math.min(1, this.phase.t / this.phase.dur));
      const ease = u * u * (3 - 2 * u);
      const forward = this.phase.from === 0;
      const fromX = forward ? this.ax : this.bx;
      const fromZ = forward ? this.az : this.bz;
      const toX = forward ? this.bx : this.ax;
      const toZ = forward ? this.bz : this.az;
      const fromY = (forward ? this.ay : this.by) + releaseH;
      const toY = (forward ? this.by : this.ay) + catchH;
      const dx = toX - fromX;
      const dz = toZ - fromZ;
      const len = Math.hypot(dx, dz) || 1;
      const sx = fromX + (dx / len) * handReach;
      const sz = fromZ + (dz / len) * handReach;
      const ex = toX - (dx / len) * handReach * 0.75;
      const ez = toZ - (dz / len) * handReach * 0.75;
      const x = sx + (ex - sx) * ease;
      const z = sz + (ez - sz) * ease;
      const arc = Math.sin(Math.PI * ease) * 0.85;
      const y = fromY + (toY - fromY) * ease + arc;
      this.disc.position.set(x, y, z);
      // Flat disc in XZ; spin about vertical; light bank into the flight.
      this.spin += dtSec * 16;
      const bank = Math.sin(ease * Math.PI) * 0.22 * (forward ? 1 : -1);
      this.disc.rotation.set(bank, this.spin, bank * 0.35);
    } else {
      const who =
        this.phase.kind === "hold" ? this.phase.holder
        : this.phase.kind === "windup" ? this.phase.thrower
        : this.phase.catcher;
      const atA = who === 0;
      const hx = atA ? this.ax : this.bx;
      const hz = atA ? this.az : this.bz;
      const hy = atA ? this.ay : this.by;
      const towardX = atA ? this.bx - this.ax : this.ax - this.bx;
      const towardZ = atA ? this.bz - this.az : this.az - this.bz;
      const len = Math.hypot(towardX, towardZ) || 1;
      const reach =
        this.phase.kind === "windup" ? handReach * 0.85
        : this.phase.kind === "catch" ? handReach * 0.55
        : handReach * 0.4;
      const height =
        this.phase.kind === "windup" ? releaseH * 0.92
        : this.phase.kind === "catch" ? catchH
        : holdH;
      this.disc.position.set(
        hx + (towardX / len) * reach,
        hy + height,
        hz + (towardZ / len) * reach,
      );
      this.spin += dtSec * (this.phase.kind === "hold" ? 1.2 : 4);
      this.disc.rotation.set(0.08, this.spin, 0.04);
    }

    // Excited catchers hop a little when no dog is right next to them.
    const wantJumpA = !dogNear && aPose === "catch";
    const wantJumpB = !dogNear && bPose === "catch";
    this.jumpA = wantJumpA
      ? Math.max(0, Math.sin(timeMs * 0.011 + this.ax) * 0.11)
      : this.jumpA * Math.max(0, 1 - dtSec * 8);
    this.jumpB = wantJumpB
      ? Math.max(0, Math.sin(timeMs * 0.011 + this.bx) * 0.11)
      : this.jumpB * Math.max(0, 1 - dtSec * 8);

    const bobA = aPose === "ready" ? 1 + Math.sin(timeMs * 0.006 + this.ax) * 0.012 : 1;
    const bobB = bPose === "ready" ? 1 + Math.sin(timeMs * 0.006 + this.bx) * 0.012 : 1;
    this.kidA.position.y = this.ay + this.readyA.height * SPRITE_FOOT_Y * bobA + this.jumpA;
    this.kidB.position.y = this.by + this.readyB.height * SPRITE_FOOT_Y * bobB + this.jumpB;

    if (aPose === "throw") {
      const toward = Math.hypot(this.bx - this.ax, this.bz - this.az) || 1;
      this.kidA.position.x = this.ax + ((this.bx - this.ax) / toward) * 0.12;
      this.kidA.position.z = this.az + ((this.bz - this.az) / toward) * 0.12;
    } else {
      this.kidA.position.x = this.ax;
      this.kidA.position.z = this.az;
    }
    if (bPose === "throw") {
      const toward = Math.hypot(this.ax - this.bx, this.az - this.bz) || 1;
      this.kidB.position.x = this.bx + ((this.ax - this.bx) / toward) * 0.12;
      this.kidB.position.z = this.bz + ((this.az - this.bz) / toward) * 0.12;
    } else {
      this.kidB.position.x = this.bx;
      this.kidB.position.z = this.bz;
    }

    faceCameraYaw(this.kidA, camera);
    faceCameraYaw(this.kidB, camera);
    const faceA = Math.atan2(this.bz - this.az, this.bx - this.ax);
    const faceB = faceA + Math.PI;
    this.kidA.scale.set(facingFlipSign(this.ax, this.az, faceA, camera), 1, 1);
    this.kidB.scale.set(facingFlipSign(this.bx, this.bz, faceB, camera), 1, 1);
  }

  destroy(): void {
    this.group.removeFromParent();
    this.kidA.geometry.dispose();
    this.kidB.geometry.dispose();
    this.disc.geometry.dispose();
    (this.disc.material as THREE.Material).dispose();
  }

  private advance(dtSec: number): void {
    this.phase.t += dtSec;
    if (this.phase.t < this.phase.dur) {
      return;
    }
    const over = this.phase.t - this.phase.dur;
    switch (this.phase.kind) {
      case "hold": {
        const thrower = this.phase.holder;
        this.phase = { kind: "windup", thrower, t: over, dur: 0.28 + Math.random() * 0.12 };
        break;
      }
      case "windup": {
        this.phase = {
          kind: "flight",
          from: this.phase.thrower,
          t: over,
          dur: 0.85 + Math.random() * 0.25,
        };
        break;
      }
      case "flight": {
        const catcher: 0 | 1 = this.phase.from === 0 ? 1 : 0;
        this.phase = { kind: "catch", catcher, t: over, dur: 0.35 + Math.random() * 0.25 };
        break;
      }
      case "catch": {
        // Sometimes linger with the disc before tossing back.
        const linger = Math.random() < 0.55 ? 0.7 + Math.random() * 1.6 : 0.25 + Math.random() * 0.45;
        this.phase = { kind: "hold", holder: this.phase.catcher, t: over, dur: linger };
        break;
      }
    }
  }
}

function poseFor(phase: Phase, who: 0 | 1): Pose {
  switch (phase.kind) {
    case "hold":
      // Holder stands ready; partner waits empty-handed (same pose, no painted disc).
      return "ready";
    case "windup":
      return phase.thrower === who ? "throw" : "catch";
    case "flight":
      // Thrower recovers; catcher waits with empty hands up.
      return phase.from === who ? "ready" : "catch";
    case "catch":
      return phase.catcher === who ? "catch" : "ready";
  }
}

function makeShadow(width: number): THREE.Mesh {
  return makeContactShadow({
    radius: Math.min(0.55, width * 0.4),
    squash: 1,
    opacity: 0.88,
  });
}

function setHumanMap(
  mesh: THREE.Mesh,
  sheet: ReturnType<typeof humanSprite>,
): void {
  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial && mat.map !== sheet.texture) {
    mat.map = sheet.texture;
    mat.needsUpdate = true;
  }
}
